import { Component, OnInit, HostListener, OnDestroy } from '@angular/core';
import { HttpService } from 'src/app/services/http-service.service';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ModalComponent } from 'src/app/shared/modal/modal.component';
import { IBranchModel } from 'src/app/logic/branch/models/branch.model';
import { IExchangeModel } from 'src/app/logic/exchanges/model/exchange.model';
import { BranchesService } from 'src/app/services/branches.service';
import { ExchangeService } from 'src/app/services/exchange.service';
import { Subscription } from 'rxjs';
import {
  HeroSectionService,
  IHeroSection,
} from 'src/app/services/hero-section.service';
import { HeroSliderController } from 'src/app/shared/utils/hero-slider.controller';
import { LanguageService } from 'src/app/services/language.service';

@Component({
  selector: 'divisas-capital-tabla-precios',
  templateUrl: './tabla-precios.component.html',
  styleUrls: ['./tabla-precios.component.scss'],
})
export class TablaPreciosComponent implements OnInit, OnDestroy {
  public getScreenWidth: number = 0;
  public tablaPrecios: any = [];
  public sucursalId: number = 0;
  public sucursales: IBranchModel[] = [];
  public sucursal: IBranchModel | undefined;
  public mxnExchange: IExchangeModel | undefined;
  public handleBranchChange$: Subscription | undefined;
  private openReserveSub$: Subscription | undefined;
  private languageSub$: Subscription | undefined;

  readonly todayLabel: string = (() => {
    const meses = [
      'Enero',
      'Febrero',
      'Marzo',
      'Abril',
      'Mayo',
      'Junio',
      'Julio',
      'Agosto',
      'Septiembre',
      'Octubre',
      'Noviembre',
      'Diciembre',
    ];
    const d = new Date();
    return `${String(d.getDate()).padStart(2, '0')}/${meses[d.getMonth()]}/${d.getFullYear()}`;
  })();

  allExchanges: IExchangeModel[] = [];

  readonly heroSlider = new HeroSliderController();

  get heroSections(): IHeroSection[] {
    return this.heroSlider.heroSections;
  }

  get isFading(): boolean {
    return this.heroSlider.isFading;
  }

  get currentSection(): IHeroSection | null {
    return this.heroSlider.currentSection;
  }

  get prevSection(): IHeroSection | null {
    return this.heroSlider.prevSection;
  }

  @HostListener('window:resize', ['$event'])
  onWindowsResize(event?: Event) {
    this.getScreenWidth = window.innerWidth;
  }

  constructor(
    private readonly _httpService: HttpService,
    private readonly exchangeService: ExchangeService,
    public dialog: MatDialog,
    public readonly _branchService: BranchesService,
    private readonly heroSectionService: HeroSectionService,
    private readonly languageService: LanguageService,
  ) {
    this.cargarSucursales();
  }

  ngOnInit(): void {
    this.getScreenWidth = window.innerWidth;

    this.handleBranchChange$ = this._branchService.sucursalId.subscribe(
      (branchId: number) => {
        if (branchId && branchId !== 0 && this.sucursalId !== branchId) {
          this.sucursalId = branchId;
          this.sucursalSeleccionada(false);
        }
      },
    );

    this.loadHeroSections();

    this.languageSub$ = this.languageService.currentLanguage$.subscribe(() => {
      this.loadHeroSections();
    });

    this.openReserveSub$ = this._branchService.openReserveDialog$.subscribe(
      () => this.openDialog(),
    );
  }

  ngOnDestroy(): void {
    if (this.handleBranchChange$) {
      this.handleBranchChange$.unsubscribe();
    }
    if (this.openReserveSub$) {
      this.openReserveSub$.unsubscribe();
    }
    if (this.languageSub$) {
      this.languageSub$.unsubscribe();
    }
    this.heroSlider.destroy();
  }

  private loadHeroSections(): void {
    this.heroSectionService.getActiveHeroSections().subscribe((sections) => {
      this.heroSlider.setSections(sections);
    });
  }

  onHeroFadeEnd(event: AnimationEvent): void {
    this.heroSlider.onFadeAnimationEnd(event.animationName);
  }

  sucursalSeleccionada(emit: boolean = true) {
    this.sucursal = this.sucursales.find(
      (value: IBranchModel) => value.id == this.sucursalId,
    );

    if (this.sucursal && emit) {
      this._branchService.setBranchSelected(this.sucursal);
    }

    this.cargarMonedas();
  }

  openDialog(): void {
    const isMobile = window.innerWidth < 768;

    const config: MatDialogConfig = {
      data: {
        sucursal: this.sucursal,
        precios: [...this.allExchanges],
        tienes: this.mxnExchange,
        quieres: this.tablaPrecios[0],
      },
      closeOnNavigation: true,
      disableClose: true,
      panelClass: 'custom-modalbox',
      position: {
        bottom: '0',
        right: '0',
      },
      maxHeight: isMobile ? '85vh' : '70vh',
    };

    this.dialog
      .open(ModalComponent, config)
      .afterClosed()
      .subscribe((result) => {});
  }

  scrollToMap(): void {
    if (this.sucursal) {
      this._branchService.setBranchSelected(this.sucursal);
    }

    const isMobile = window.innerWidth < 768;
    const mapId = isMobile ? 'map-section-mobile' : 'map-section';
    const el = document.getElementById(mapId);

    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    document.getElementById('branches')?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  }

  cargarSucursales() {
    this._branchService.getAll().subscribe((branches: any) => {
      this.sucursales = branches;

      this._branchService.getBranchSelected().subscribe((branch) => {
        if (branch) {
          this.sucursal = branch;
          this.sucursalId = branch.id;

          this.sucursalSeleccionada(true);
        }
      });
    });
  }

  cargarMonedas() {
    this.exchangeService
      .getBranchExchangeRates(this.sucursalId)
      .subscribe((monedas: any) => {
        const exchangeList = structuredClone(monedas.exchange).sort(
          (a: any, b: any) =>
            Number(a?.currency?.order ?? 0) - Number(b?.currency?.order ?? 0),
        );
        this.allExchanges = exchangeList;

        this.mxnExchange = exchangeList.find(
          (value: any) => value.currency?.code === 'MXN',
        );
        this.tablaPrecios = exchangeList.filter(
          (value: any) => value.currency?.code !== 'MXN',
        );
      });
  }
}
